import {DndContext, closestCenter, DragOverlay} from "@dnd-kit/core";
import {SortableContext, rectSortingStrategy} from "@dnd-kit/sortable";
import {cn} from "@/lib/utils";
import {SortableItem} from "./sortable-item";
import type {DndContextProps} from "@dnd-kit/core";

interface SortableGridProps<T extends {id: string}> {
  items: T[];
  renderItem: (
    item: T,
    index: number,
    options: {isEditMode: boolean; isDragging: boolean; isActiveItem: boolean}
  ) => React.ReactNode;
  isEditMode: boolean;
  isDragging: boolean;
  activeId?: string | null;
  activeItem?: T;
  sensors: DndContextProps["sensors"];
  onDragStart: DndContextProps["onDragStart"];
  onDragEnd: DndContextProps["onDragEnd"];
  onDragCancel: DndContextProps["onDragCancel"];
  onDeleteItem?: (id: string) => void;
  isDeleting?: boolean;
  className?: string;
  gridClassName?: string;
}

export function SortableGrid<T extends {id: string}>({
  items,
  renderItem,
  isEditMode,
  isDragging,
  activeId,
  activeItem,
  sensors,
  onDragStart,
  onDragEnd,
  onDragCancel,
  onDeleteItem,
  isDeleting,
  className,
  gridClassName = "grid gap-4 lg:grid-cols-2 2xl:grid-cols-3",
}: SortableGridProps<T>) {
  const itemIds = items.map((item) => item.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={itemIds} strategy={rectSortingStrategy}>
        {/* Extra padding to prevent clipping of delete buttons and rotated cards */}
        <div className={cn(gridClassName, className)}>
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              isEditMode={isEditMode}
              isDragging={isDragging}
              onDelete={onDeleteItem ? () => onDeleteItem(item.id) : undefined}
              isDeleting={isDeleting}
            >
              {renderItem(item, index, {
                isEditMode,
                isDragging,
                isActiveItem: item.id === activeId,
              })}
            </SortableItem>
          ))}
        </div>
      </SortableContext>

      {/* Drag overlay - shows the card following cursor during drag */}
      <DragOverlay dropAnimation={null}>
        {activeItem && (
          <div className="scale-105 rotate-1 cursor-grabbing opacity-90 shadow-2xl shadow-black/30">
            {renderItem(activeItem, -1, {
              isEditMode: false,
              isDragging: true,
              isActiveItem: true,
            })}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
